/**
 * Settings Page
 * პარამეტრების გვერდი
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Button, Input, Spinner, useAuth, useNotification } from '@aiia/portal-core';
import { useCurrentUser, useUpdateProfile, useDeleteAccount, useUsage, useSubscription } from '../hooks';
import type { EntityType, UpdateProfileRequest } from '../features/api/user';
import { Link } from 'react-router-dom';
import { useLocalizedPath } from '../hooks/useLocalizedPath';
import { getApiErrorMessage } from '../utils/errorMessages';
import { formatDate } from '../utils/formatDate';

export default function SettingsPage() {
  const { t } = useTranslation();
  const lp = useLocalizedPath();
  const { logout } = useAuth();
  const notification = useNotification();
  const { user, isLoading: loadingUser, refetch: refetchUser } = useCurrentUser();
  const updateProfile = useUpdateProfile();

  // Get user ID for subscription hooks
  const userId = user?.id || '';
  const { subscription, isLoading: loadingSubscription } = useSubscription(userId);
  const { usage, isLoading: loadingUsage } = useUsage(userId);

  const deleteAccountMutation = useDeleteAccount();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<UpdateProfileRequest>({
    entityType: 'INDIVIDUAL',
    firstName: '',
    lastName: '',
    personalId: '',
    companyName: '',
    companyId: '',
    phone: '',
    address: '',
  });

  // Update form when user data loads
  useEffect(() => {
    if (user) {
      setFormData({
        entityType: user.entityType || 'INDIVIDUAL',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        personalId: user.personalId || '',
        companyName: user.companyName || '',
        companyId: user.companyId || '',
        phone: user.phone || '',
        address: user.address || '',
      });
    }
  }, [user]);

  const handleSaveProfile = async () => {
    try {
      // Send empty strings as null so backend clears irrelevant fields
      const payload: UpdateProfileRequest = {
        ...formData,
        personalId: formData.personalId || null,
        companyName: formData.companyName || null,
        companyId: formData.companyId || null,
      };
      await updateProfile.mutateAsync(payload);
      notification.success(t('settings.profileUpdated'));
      setIsEditing(false);
      refetchUser();
    } catch (error: unknown) {
      notification.error(getApiErrorMessage(error, t));
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await deleteAccountMutation.mutateAsync();
      logout();
    } catch (error: unknown) {
      notification.error(getApiErrorMessage(error, t));
    }
  };

  const handleEntityTypeChange = (type: EntityType) => {
    setFormData(prev => ({ ...prev, entityType: type }));
  };

  if (loadingUser) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('settings.title')}</h1>
        <p className="text-gray-600 mt-1">{t('settings.subtitle')}</p>
      </div>

      {/* Profile Completion Alert */}
      {user && !user.profileComplete && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-yellow-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <p className="font-medium text-yellow-800">{t('settings.profileIncomplete')}</p>
              <p className="text-sm text-yellow-700">{t('settings.profileIncompleteDesc')}</p>
            </div>
          </div>
        </div>
      )}

      {/* User Profile Section */}
      <Card className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">{t('settings.userProfile')}</h2>
          {!isEditing ? (
            <Button variant="secondary" onClick={() => setIsEditing(true)}>
              {t('settings.editProfile')}
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => setIsEditing(false)}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleSaveProfile} disabled={updateProfile.isPending}>
                {updateProfile.isPending ? t('common.saving') : t('settings.saveChanges')}
              </Button>
            </div>
          )}
        </div>

        {/* Avatar and Basic Info */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
            <span className="text-2xl font-semibold text-blue-600">
              {user?.firstName?.[0] || user?.email?.[0]?.toUpperCase() || 'U'}
            </span>
          </div>
          <div>
            <p className="font-medium text-gray-900">
              {user?.displayName || user?.email}
            </p>
            <p className="text-sm text-gray-500">{user?.email}</p>
            {user?.profileComplete ? (
              <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded-full mt-1">
                {t('settings.profileComplete')}
              </span>
            ) : (
              <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full mt-1">
                {t('settings.profileIncompleteStatus')}
              </span>
            )}
          </div>
        </div>

        {/* Entity Type Selection */}
        {isEditing && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('settings.accountType')}</label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="entityType"
                  checked={formData.entityType === 'INDIVIDUAL'}
                  onChange={() => handleEntityTypeChange('INDIVIDUAL')}
                  className="mr-2"
                />
                <span>{t('settings.individual')}</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="entityType"
                  checked={formData.entityType === 'LEGAL_ENTITY'}
                  onChange={() => handleEntityTypeChange('LEGAL_ENTITY')}
                  className="mr-2"
                />
                <span>{t('settings.legalEntity')}</span>
              </label>
            </div>
          </div>
        )}

        {/* Form Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Individual Fields */}
          {(formData.entityType === 'INDIVIDUAL' || !isEditing) && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.firstName')}</label>
                {isEditing ? (
                  <Input
                    value={formData.firstName}
                    onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                    placeholder={t('settings.enterFirstName')}
                  />
                ) : (
                  <p className="px-3 py-2 bg-gray-50 rounded-lg text-gray-900">{user?.firstName || '-'}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.lastName')}</label>
                {isEditing ? (
                  <Input
                    value={formData.lastName}
                    onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                    placeholder={t('settings.enterLastName')}
                  />
                ) : (
                  <p className="px-3 py-2 bg-gray-50 rounded-lg text-gray-900">{user?.lastName || '-'}</p>
                )}
              </div>
              {formData.entityType === 'INDIVIDUAL' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.personalId')}</label>
                  {isEditing ? (
                    <Input
                      value={formData.personalId || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, personalId: e.target.value }))}
                      placeholder={t('settings.enterPersonalId')}
                    />
                  ) : (
                    <p className="px-3 py-2 bg-gray-50 rounded-lg text-gray-900">{user?.personalId || '-'}</p>
                  )}
                </div>
              )}
            </>
          )}

          {/* Company Fields */}
          {formData.entityType === 'LEGAL_ENTITY' && isEditing && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.companyName')}</label>
                <Input
                  value={formData.companyName || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, companyName: e.target.value }))}
                  placeholder={t('settings.enterCompanyName')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.companyId')}</label>
                <Input
                  value={formData.companyId || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, companyId: e.target.value }))}
                  placeholder={t('settings.enterCompanyId')}
                />
              </div>
            </>
          )}

          {!isEditing && user?.entityType === 'LEGAL_ENTITY' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.companyName')}</label>
                <p className="px-3 py-2 bg-gray-50 rounded-lg text-gray-900">{user?.companyName || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.companyId')}</label>
                <p className="px-3 py-2 bg-gray-50 rounded-lg text-gray-900">{user?.companyId || '-'}</p>
              </div>
            </>
          )}

          {/* Common Fields */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.phone')}</label>
            {isEditing ? (
              <Input
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+995 XXX XXX XXX"
              />
            ) : (
              <p className="px-3 py-2 bg-gray-50 rounded-lg text-gray-900">{user?.phone || '-'}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.email')}</label>
            <p className="px-3 py-2 bg-gray-50 rounded-lg text-gray-500">{user?.email || '-'}</p>
            {isEditing && <p className="text-xs text-gray-400 mt-1">{t('settings.emailCannotChange')}</p>}
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.address')}</label>
            {isEditing ? (
              <Input
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                placeholder={t('settings.enterAddress')}
              />
            ) : (
              <p className="px-3 py-2 bg-gray-50 rounded-lg text-gray-900">{user?.address || '-'}</p>
            )}
          </div>
        </div>

        <div className="mt-6 pt-4 border-t">
          <Button variant="secondary" onClick={logout}>
            {t('settings.signOut')}
          </Button>
        </div>
      </Card>

      {/* Subscription Section */}
      <Card className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">{t('settings.subscription')}</h2>
          <Link to={lp('/subscription')}>
            <Button>{t('settings.manageSubscription')}</Button>
          </Link>
        </div>

        {loadingSubscription ? (
          <div className="flex justify-center py-4">
            <Spinner />
          </div>
        ) : subscription ? (
          <>
            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg mb-4">
              <div>
                <p className="font-medium text-gray-900">
                  {t('settings.currentPlan', { plan: subscription.planName })}
                </p>
                <p className="text-sm text-gray-500">
                  {subscription.status === 'TRIAL' ? (
                    <>{t('settings.trialEnds', { date: formatDate(subscription.trialEndsAt) })}</>
                  ) : (
                    <>{t('settings.renews', { date: formatDate(subscription.currentPeriodEnd) })}</>
                  )}
                </p>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                subscription.status === 'ACTIVE' ? 'bg-green-100 text-green-800' :
                subscription.status === 'TRIAL' ? 'bg-blue-100 text-blue-800' :
                subscription.status === 'CANCELED' ? 'bg-red-100 text-red-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {subscription.status}
              </span>
            </div>

            {/* Usage Stats */}
            {loadingUsage ? (
              <div className="flex justify-center py-4">
                <Spinner />
              </div>
            ) : usage && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">{t('settings.conversations')}</p>
                  <p className="text-xl font-bold text-gray-900">
                    {usage.conversationsUsed} / {usage.conversationsLimit === -1 ? '∞' : usage.conversationsLimit}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">{t('settings.apiCalls')}</p>
                  <p className="text-xl font-bold text-gray-900">
                    {usage.apiCallsUsed} / {usage.apiCallsLimit === -1 ? '∞' : usage.apiCallsLimit}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">{t('settings.widgetsUsage')}</p>
                  <p className="text-xl font-bold text-gray-900">
                    {usage.widgetsUsed} / {usage.widgetsLimit === -1 ? '∞' : usage.widgetsLimit}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">{t('settings.apiKeysUsage')}</p>
                  <p className="text-xl font-bold text-gray-900">
                    {usage.apiKeysUsed} / {usage.apiKeysLimit === -1 ? '∞' : usage.apiKeysLimit}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">{t('settings.dataFilesUsage')}</p>
                  <p className="text-xl font-bold text-gray-900">
                    {usage.dataFilesUsed} / {usage.dataFilesLimit === -1 ? '∞' : usage.dataFilesLimit}
                  </p>
                </div>
              </div>
            )}
          </>
        ) : (
          <p className="text-gray-500">{t('settings.noSubscription')}</p>
        )}
      </Card>

      {/* Danger Zone */}
      <Card className="p-6 border-red-200">
        <h2 className="text-lg font-semibold text-red-600 mb-4">{t('settings.dangerZone')}</h2>
        <div className="p-4 bg-red-50 rounded-lg">
          <p className="font-medium text-gray-900 mb-2">{t('settings.deleteAccount')}</p>
          <p className="text-sm text-gray-500 mb-4">
            {t('settings.deleteAccountWarning')}
          </p>
          <Button variant="danger" onClick={() => setShowDeleteModal(true)}>
            {t('settings.deleteAccount')}
          </Button>
        </div>
      </Card>

      {/* Delete Account Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-red-600 mb-2">
              {t('settings.deleteAccount')}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {t('settings.deleteAccountConfirmation')}
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('settings.deleteAccountTypeDelete')}
              </label>
              <Input
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                placeholder="DELETE"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmation('');
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="danger"
                disabled={deleteConfirmation !== 'DELETE' || deleteAccountMutation.isPending}
                onClick={handleDeleteAccount}
              >
                {deleteAccountMutation.isPending ? t('settings.deleting') : t('settings.deleteAccount')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
